/**
 * Gamma exposure, read as a day-trading playbook rather than a data dump.
 *
 * The numbers alone don't tell you what to do, and a static legend goes stale
 * the moment the regime changes. So this derives the regime from live price vs
 * the flip and states the implication for THAT regime — the guidance flips when
 * the market does.
 *
 * Every level here is already in futures points; the engine converted them.
 */
import React, { useMemo, useState } from 'react';
import { useGexStore, selectGexSlot } from '../../store/gexStore';
import type { GexUnderlying } from '../../store/gexStore';
import './GexInfoOverlay.css';

interface GexInfoOverlayProps {
  /** Live futures price. Without it there is no regime to report. */
  currentPrice: number | null;
  /** The chart's index (NDX for NQ/MNQ, SPX for ES/MES); null = no GEX for this chart. */
  underlying: GexUnderlying | null;
}

/** How a level behaves, and what that means for an entry. */
interface LevelRow {
  key: string;
  label: string;
  price: number;
  /** Signed distance from spot, in futures points. */
  distance: number;
  tone: 'resistance' | 'support' | 'regime';
}

const fmt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 0 });
const signed = (n: number) => `${n >= 0 ? '+' : ''}${fmt(n)}`;

const GexInfoOverlay: React.FC<GexInfoOverlayProps> = React.memo(({ currentPrice, underlying }) => {
  const { levels, available, reason } = useGexStore(selectGexSlot(underlying));
  const [showPlaybook, setShowPlaybook] = useState(false);

  const view = useMemo(() => {
    if (!levels || !currentPrice || currentPrice <= 0) return null;

    // The regime is the single most actionable fact here, so it is derived
    // first and everything else is framed around it. Unknown when there is no
    // flip — a chain can be single-signed across the whole searched band, and
    // guessing a regime from a missing level would be worse than saying so.
    const flip = levels.gammaFlip;
    const regime: 'positive' | 'negative' | 'unknown' =
      flip == null ? 'unknown' : currentPrice >= flip ? 'positive' : 'negative';

    const rows: LevelRow[] = [];
    if (levels.callWall != null) {
      rows.push({
        key: 'call',
        label: 'Call wall',
        price: levels.callWall,
        distance: levels.callWall - currentPrice,
        tone: 'resistance',
      });
    }
    if (flip != null) {
      rows.push({
        key: 'flip',
        label: 'Zero gamma',
        price: flip,
        distance: flip - currentPrice,
        tone: 'regime',
      });
    }
    if (levels.putWall != null) {
      rows.push({
        key: 'put',
        label: 'Put wall',
        price: levels.putWall,
        distance: levels.putWall - currentPrice,
        tone: 'support',
      });
    }
    rows.sort((a, b) => b.price - a.price);

    // Share of absolute gamma sitting in the front week. Intraday hedging is
    // driven by near-dated gamma; if almost none of the book is near-dated,
    // these levels describe positioning that will not trade today.
    // Both of these arrived in a later engine build than the levels themselves,
    // and a socket payload or a persisted chain from before it has neither. An
    // overlay that throws takes the whole chart panel with it, so absence is
    // handled as "don't show that row" rather than trusted to be present.
    const buckets = levels.termStructure ?? [];
    const totalAbs = buckets.reduce((t, b) => t + Math.abs(b.netGex), 0);
    const nearAbs = buckets
      .filter((b) => b.label === '0-7d')
      .reduce((t, b) => t + Math.abs(b.netGex), 0);
    const nearPct = totalAbs > 0 ? (nearAbs / totalAbs) * 100 : null;

    const ageMin = levels.fetchedAt ? (Date.now() - levels.fetchedAt) / 60000 : null;

    // Exactly zero is not a directional lean, it is an absent reading — the
    // shape an upstream-zeroed chain produces. Rendering it as "long" would
    // state a bias the data does not contain.
    const netDex =
      Number.isFinite(levels.netDex) && levels.netDex !== 0 ? (levels.netDex as number) : null;

    return { regime, rows, nearPct, ageMin, netDex, flip };
  }, [levels, currentPrice]);

  // No index options behind this chart's symbol (anything but NQ/MNQ/ES/MES).
  if (!underlying) return null;

  if (!view) {
    return (
      <div className="gex-info-overlay">
        <div className="gex-info-header">
          <span className="gex-info-title">Gamma · {underlying}</span>
          <span className="gex-info-badge unavailable">n/a</span>
        </div>
        <div className="gex-info-empty">{reason ?? 'waiting for chain'}</div>
      </div>
    );
  }

  const { regime, rows, nearPct, ageMin, netDex } = view;

  const regimeHeadline =
    regime === 'positive'
      ? 'POSITIVE GAMMA'
      : regime === 'negative'
        ? 'NEGATIVE GAMMA'
        : 'NO FLIP NEARBY';

  // The one line that should drive the next trade.
  // Deliberately terse. This sits over live candles, so it has to be readable at
  // a glance without becoming a paragraph across the chart; the reasoning is one
  // click away under `?`.
  const regimeAction =
    regime === 'positive'
      ? 'fade extremes · breakouts fail'
      : regime === 'negative'
        ? 'go with momentum · wider stops'
        : 'no regime edge to read';

  return (
    <div className={`gex-info-overlay${showPlaybook ? ' expanded' : ''}`}>
      <div className="gex-info-header">
        <span className="gex-info-title">Gamma · {underlying}</span>
        <span className={`gex-info-badge ${available ? 'live' : 'stale'}`}>
          {available ? 'live' : 'stale'}
        </span>
        {ageMin != null && ageMin > 1 && (
          <span className="gex-info-age">{Math.round(ageMin)}m old</span>
        )}
        <button
          className="gex-info-help"
          onClick={() => setShowPlaybook((v) => !v)}
          title="How to trade these"
        >
          {showPlaybook ? '−' : '?'}
        </button>
      </div>

      <div className={`gex-info-regime ${regime}`}>
        <div className="gex-info-regime-name">{regimeHeadline}</div>
        <div className="gex-info-regime-action">{regimeAction}</div>
      </div>

      <div className="gex-info-levels">
        {rows.map((r) => (
          <div key={r.key} className={`gex-info-level ${r.tone}`}>
            <span className="gex-info-level-label">{r.label}</span>
            <span className="gex-info-level-price">{fmt(r.price)}</span>
            <span className="gex-info-level-dist">{signed(r.distance)}</span>
          </div>
        ))}
      </div>

      <div className="gex-info-secondary">
        {netDex != null && (
          <span className="gex-info-stat">
            <span className="gex-info-stat-label">DEX</span>
            <span className={`gex-info-stat-value ${netDex >= 0 ? 'pos' : 'neg'}`}>
              {netDex >= 0 ? '+' : ''}
              {(netDex / 1e9).toFixed(1)}B
            </span>
            <span className="gex-info-stat-note">{netDex >= 0 ? 'long' : 'short'}</span>
          </span>
        )}
        {nearPct != null && (
          <span className="gex-info-stat">
            <span className="gex-info-stat-label">0-7d</span>
            <span className="gex-info-stat-value">{nearPct.toFixed(0)}%</span>
            <span className="gex-info-stat-note">
              {nearPct < 25 ? 'thin — levels weak today' : 'near-dated'}
            </span>
          </span>
        )}
      </div>

      {showPlaybook && (
        <div className="gex-info-playbook">
          <div className="gex-info-playbook-row">
            <strong>Above the flip</strong> dealers are long gamma and hedge
            against the move — selling rallies, buying dips. The tape
            mean-reverts. Fade pushes toward the walls; expect breakouts to
            fail. This is where a small fixed target fills by reversion.
          </div>
          <div className="gex-info-playbook-row">
            <strong>Below the flip</strong> dealers are short gamma and hedge
            with the move — selling weakness, buying strength. The tape trends
            and ranges expand. Do not fade. Momentum and breakouts work, and
            stops need more room for the same setup.
          </div>
          <div className="gex-info-playbook-row">
            <strong>Walls are targets, not entries.</strong> They are where
            hedging is heaviest, so price
            stalls there. They make good profit targets and poor places to
            enter in the direction of the wall. A decisive break through one
            often runs, because the hedging that was pinning price drops away
            past it.
          </div>
          <div className="gex-info-playbook-row">
            <strong>Crossing the flip</strong> is the signal to change tactics,
            not a trade on its own. The same setup behaves differently on
            either side of it.
          </div>
          <div className="gex-info-playbook-row caveat">
            <strong>Trust limits.</strong> The chain is ~15 min delayed and open
            interest is yesterday's settle, so these are structural levels for
            the session, not tick-by-tick signals. Dealer direction is inferred
            from a standard convention (long calls, short puts), not from real
            flow. The walls were checked against an independent vendor and
            matched exactly; the flip depends on how much of the expiry curve
            you include and ours uses 45 days. None of this has been tested for
            edge on your setup yet.
          </div>
        </div>
      )}
    </div>
  );
});

GexInfoOverlay.displayName = 'GexInfoOverlay';

export default GexInfoOverlay;
